import React, { useState, useEffect, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import JBImage from 'components/essentials/JBImage';
import JBIFrameHolder from 'components/essentials/JBIFrameHolder';
import CtaButton from 'components/essentials/Cta/CtaButton';
import CtaLink from 'components/essentials/Cta/CtaLink';
import VideoPlayButton from 'components/essentials/VideoPlayButton';
import ToggleCTALinkButton from 'components/essentials/ToggleCTALinkButton';
import useIsBrowser from 'hooks/useIsBrowser';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import MobileOnly from '../MobileOnly';
import TabletAndDesktopOnly from '../TabletAndDesktopOnly';

const ImageBanner = props => {
  const [isPlaying, setPlaying] = useState(true);
  const [iFrameVisibility, setiFrameVisibility] = useState(false);
  const videoRef = useRef(null);

  const isBrowser = useIsBrowser();
  const renderDefaultInNewConsumerApp = isNewConsumerApp && !isBrowser;

  const isDeviceBelow426px = useMediaQuery({
    query: '(max-width: 425px)',
  });

  const isBelow426px = renderDefaultInNewConsumerApp
    ? true
    : isDeviceBelow426px;

  const isDeviceBelow2000px = useMediaQuery({
    query: '(max-width: 1999px)',
  });

  const isBelow2000px = renderDefaultInNewConsumerApp
    ? true
    : isDeviceBelow2000px;

  useEffect(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [isPlaying]);

  if (!props.config || !Object.keys(props.config).length) return null;

  const {
    imageProps,
    videoBlock,
    textColor,
    subHeading,
    heading,
    subText,
    cta,
    textOrientation,
    showOverlay,
    styling,
    minHeight,
    toggleInputType,
    imageComponent,
  } = props.config;
  const orientationValue = textOrientation?.value
    ? textOrientation.value
    : 'left';
  const orientationLowerCase = orientationValue
    .toLowerCase()
    .split(' ')
    .join('-');
  const overlayOpacity =
    showOverlay && showOverlay.value ? parseInt(showOverlay.value, 10) : null;
  const finalStyling =
    !!styling?.value && styling.value !== 'Default' ? styling.value : '';

  const HeaderTag = heading.tagName || 'h2';
  const SubHeadingTag = heading.tagName === 'h1' ? 'h2' : 'h1';

  const getImageQualityFactor = () => {
    if (isBelow426px) {
      return 20;
    }
    if (isBelow2000px) {
      return 40;
    }
    return 60;
  };

  return (
    <div
      className={classnames(
        'flex font-sans mx-auto my-0 relative w-full px-5 py-12 md:px-[3.333vw] md:py-8 lg:p-20 print:hidden',
        finalStyling,
        {
          'w-full': imageComponent?.size !== 'Full Page',
          'max-w-[1400px]': imageComponent?.size === 'Full Page',
          'flex-row': orientationLowerCase !== 'right',
          'content-start': orientationLowerCase === 'left',
          'flex-row-reverse': orientationLowerCase === 'right',
          'items-start': orientationLowerCase === 'top-left',
          'items-start flex-row-reverse': orientationLowerCase === 'top-right',
          'items-start justify-center': orientationLowerCase === 'top-center',
          'items-end': orientationLowerCase === 'bottom-left',
          'items-end flex-row-reverse': orientationLowerCase === 'bottom-right',
          'items-end justify-center': orientationLowerCase === 'bottom-center',
          'justify-center': orientationLowerCase === 'center',
        }
      )}
      style={{
        minHeight: `${minHeight && minHeight.value ? minHeight.value : '65vh'}`,
      }}
    >
      <div className="h-full left-0 absolute top-0 w-full">
        {overlayOpacity ? (
          <div
            className="h-full left-0 absolute top-0 w-full bg-black [content:''] z-[1]"
            style={{
              opacity: `${overlayOpacity ? overlayOpacity / 100 : 0}`,
            }}
          />
        ) : null}
        {videoBlock && videoBlock.enabled.value === 'Enabled' ? (
          <div className="ImageBanner__video-block">
            <TabletAndDesktopOnly>
              <video
                ref={el => {
                  videoRef.current = el;
                  return videoRef.current;
                }}
                controls={false}
                onClick={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                className="max-[767px]:hidden h-full left-0 max-w-full object-cover object-center absolute top-0 w-full"
                key={videoBlock.videoProps.linkTo}
                autoPlay
                muted
              >
                <source src={videoBlock.videoProps.linkTo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>

              <div
                className={classnames(
                  'max-[767px]:hidden absolute bottom-4 right-4 z-[1200] w-[140px] transition-[opacity_.2s_ease-in-out] opacity-100'
                )}
              >
                <VideoPlayButton
                  isPlaying={isPlaying}
                  styling="outline-button"
                  sizes="sm"
                  onClick={() => setPlaying(!isPlaying)}
                >
                  {isPlaying ? 'Pause' : 'Play'}
                </VideoPlayButton>
              </div>
            </TabletAndDesktopOnly>

            <MobileOnly>
              <JBImage
                src={
                  videoBlock.imageProps && videoBlock.imageProps.src
                    ? videoBlock.imageProps.src
                    : null
                }
                sizes="100vw"
                className="md:hidden h-full left-0 max-w-full object-cover object-center absolute top-0 w-full"
                alt=""
                lazy={false}
              />
            </MobileOnly>
          </div>
        ) : (
          <JBImage
            src={imageProps ? imageProps.src : null}
            sizes="100vw"
            className="h-full left-0 max-w-full object-cover object-center absolute top-0 w-full"
            alt=""
            quality={getImageQualityFactor()}
            lazy={false}
            priority
          />
        )}
      </div>

      <div
        className={classnames(
          'content-center flex flex-col max-w-[50%] z-[1] justify-center',
          {
            'max-[767px]:max-w-none': orientationLowerCase === 'left',
            'text-right max-[767px]:max-w-none':
              orientationLowerCase === 'right',
            'text-right':
              orientationLowerCase === 'top-right' ||
              orientationLowerCase === 'bottom-right',
            'text-center':
              orientationLowerCase === 'top-center' ||
              orientationLowerCase === 'bottom-center',
            'items-end': orientationLowerCase === 'bottom-left',
            'text-center max-[767px]:max-w-none':
              orientationLowerCase === 'center',
          }
        )}
        style={{ color: textColor?.value ?? 'white' }}
      >
        {subHeading ? (
          <SubHeadingTag className="text-sm font-bold uppercase mt-0 mx-0 mb-[.5em] text-inherit">
            {subHeading.content}
          </SubHeadingTag>
        ) : null}

        <HeaderTag className="m-0 text-[clamp(1.75rem,5.2vw,2.5rem)] leading-[clamp(2.25rem,6.25vw,3rem)] tracking-[.0125rem] font-bold text-inherit">
          {heading.content}
        </HeaderTag>

        <div className="text-lg mt-3 mx-0 mb-6 font-normal text-inherit">
          {subText.content}
        </div>

        {!!cta && cta.content && (!cta.embedCode || !cta.embedCode.enabled) && (
          <div>
            <CtaLink to={cta.linkTo} styling={cta.style} color="brand">
              {cta.content}
            </CtaLink>
          </div>
        )}

        {!!toggleInputType && (
          <ToggleCTALinkButton toggleInputType={toggleInputType} />
        )}

        {!toggleInputType && !!cta && cta.embedCode && cta.embedCode.enabled && (
          <div>
            {iFrameVisibility ? (
              <JBIFrameHolder
                isOpen={iFrameVisibility}
                onClose={setiFrameVisibility}
                script={cta.embedCode.value || ''}
              />
            ) : null}
            <CtaButton
              onClick={() => {
                setiFrameVisibility(true);
              }}
              color={cta.color || 'brand'}
              styling={cta.style || 'solid-button'}
            >
              {`${cta.content}>`}
            </CtaButton>
          </div>
        )}
      </div>
    </div>
  );
};

ImageBanner.propTypes = {
  config: PropTypes.shape({
    imageComponent: PropTypes.shape({
      size: PropTypes.string,
    }),
    backgroundSize: PropTypes.shape({
      value: PropTypes.string,
    }),
    cta: PropTypes.shape({
      color: PropTypes.string,
      content: PropTypes.string,
      embedCode: PropTypes.shape({
        enabled: PropTypes.string,
        value: PropTypes.string,
      }),
      linkTo: PropTypes.string,
      style: PropTypes.string,
    }),
    heading: PropTypes.shape({
      content: PropTypes.string,
      tagName: PropTypes.string,
    }),
    imageProps: PropTypes.shape({
      src: PropTypes.string,
    }),
    minHeight: PropTypes.shape({
      value: PropTypes.string,
    }),
    orientation: PropTypes.string,
    overlayOpacity: PropTypes.number,
    showOverlay: PropTypes.bool,
    styling: PropTypes.string,
    textColor: PropTypes.shape({
      value: PropTypes.string,
    }),
    subHeading: PropTypes.shape({
      content: PropTypes.string,
    }),
    subText: PropTypes.shape({
      content: PropTypes.string,
    }),
    textOrientation: PropTypes.string,
    toggleInputType: PropTypes.string,
    videoBlock: PropTypes.shape({
      enabled: PropTypes.string,
      videoProps: PropTypes.shape({
        linkTo: PropTypes.string,
      }),
      imageProps: PropTypes.shape({
        src: PropTypes.string,
      }),
    }),
  }),
};

export default ImageBanner;
